import {
  NJSpinner,
  NJToastService,
} from "@engie-group/fluid-design-system-react";
import { ChatError } from "./chatError";

import { NJChatBlock } from "./components/NJChatBlock";
import { messageSource } from "./components/NJChatBlock";
import { NJChatFeedback } from "./components/NJChatFeedback";

import { MarkdownMessage } from "./message";
import { MessageSource } from "./messageSource";
import styles from "./page.module.css";
import { ChatId, ChatMessage, Source, PDFSource, PPTSource, status } from '../model';
import { useQuery } from "@tanstack/react-query";
import { fetchSources } from "./fetchSources";
import { AssistantId } from "@genai/assistant";
import { editChat } from "./editChat.ts";

export const AssistantMessage = (props: {
  assistantId: AssistantId;
  message: ChatMessage;
  chatId?: ChatId;
}) => {
  const { assistantId, chatId, message: baseMessage } = props;
  const { data } = useQuery({
    queryKey: ["sources", assistantId, chatId, baseMessage.uuid],
    queryFn: () =>
      !assistantId || !chatId
        ? []
        : fetchSources(assistantId, chatId, message.uuid),
  });

  const message: ChatMessage = {
    ...baseMessage,
    sources: baseMessage.sources || data,
  };

// Filter out duplicate PDF/PPT sources (but allow Word always)
  let filterMessageSources: Source[] = [];

  if (message.sources) {
    const seen = new Set<string>();

    filterMessageSources = message.sources.filter(item => {
      // Always allow WordSource
      if (
        item.mimetype === "application/msword" ||
        item.mimetype === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ) {
        return true;
      }

      // Only PDF or PPT sources can have "pages"
      if (
        (item.mimetype === "application/pdf" ||
          item.mimetype === "application/vnd.openxmlformats-officedocument.presentationml.presentation") &&
        (!item.pages || item.pages.length === 0)
      ) {
        return true;
      }

      // If item has pages, check if already seen
      if (
        (item.mimetype === "application/pdf" ||
          item.mimetype === "application/vnd.openxmlformats-officedocument.presentationml.presentation") &&
        item.pages &&
        item.pages.length > 0
      ) {
        const key = `${item.fileId}-${item.pages[0]}`;

        if (seen.has(key)) {
          return false;
        }

        seen.add(key);
        return true;
      }
      return false;
    });
  }

  const groupedData: Source[] = Object.values(
    filterMessageSources.reduce((acc: Record<string, Source>, source: Source) => {
      const { fileId, collectionId, mimetype } = source;

      if (!acc[fileId]) {
        acc[fileId] = {
          fileId,
          collectionId,
          mimetype,
          ...(source.mimetype === "application/pdf" ||
          source.mimetype === "application/vnd.openxmlformats-officedocument.presentationml.presentation"
            ? { pages: [] }
            : {}),
          ...(source.mimetype === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
          source.mimetype === "application/msword"
            ? { htmlContent: source.htmlContent }
            : {}),
        } as Source;
      }

      if ("pages" in source && source.pages) {
        (acc[fileId] as PDFSource | PPTSource).pages?.push(...source.pages);
      }

      return acc;
    }, {} as Record<string, Source>)
  );

  return (
    <NJChatBlock
      className={styles["genai-chat-ai-chat-exchange-received"]}
      source={messageSource.received}
    >
      <>
        {
          {
            [status.pending]: <NJSpinner scale="md" aria-label="" />,
            [status.error]: <ChatError message={message.error} />,
            [status.success]: (
              <>
                <MarkdownMessage content={message.content} />
                {groupedData.length > 0
                  ? groupedData.map((source, index) => (
                      <MessageSource
                        key={`${source.fileId}-${source.collectionId}-${
                          "pages" in source && source.pages
                            ? source.pages.join("-")
                            : "htmlContent" in source && source.htmlContent
                              ? source.htmlContent.slice(0, 20)
                              : crypto.randomUUID()
                        }-${index}`}
                        source={source}
                      ></MessageSource>
                    ))
                  : null}
              </>
            ),
            [status.streaming]: <MarkdownMessage content={message.content} />,
          }[message.status]
        }

        {message.status === status.success ? (
          <>
            <div style={{ display: "flex", alignItems: "center" }}>
              <NJChatFeedback
                onCopyClicked={() => {
                  navigator.clipboard.writeText(message.content).catch(void 0);
                }}
              ></NJChatFeedback>

              <NJChatFeedback
                onLikeClicked={() => {
                  chatId ? editChat(assistantId, chatId, message.uuid, { rating: 1 })
                    .then(() => NJToastService("Votre avis a été enregistré. Merci !"))
                    .catch(() => NJToastService("Erreur lors de l'enregistrement de votre avis !"))
                    : null;
                }}
              ></NJChatFeedback>

              <NJChatFeedback
                onDislikeClicked={() => {
                  chatId ? editChat(assistantId, chatId, message.uuid, { rating: 2 })
                    .then(() => NJToastService("Votre avis a été enregistré. Merci !"))
                    .catch(() => NJToastService("Erreur lors de l'enregistrement de votre avis !"))
                    : null;
                }}
              ></NJChatFeedback>
            </div>

            {/* TODO: Request Style appending to InlineMessage */}
            <div style={{ marginBottom: "16px" }}></div>
          </>
        ) : null}
      </>
    </NJChatBlock>
  );
};
